import { useState, useEffect } from "react";
import GradientBorderRadius from "@/ui/gradient-border-radius";
import { IconComponent } from "@/ui/icon";

interface BuySellOptionsProps {
  value: string;
  onOptionSelect: (value: string) => void;
  isNativeToken?: boolean;
}

const BuySellOptions = ({
  isNativeToken = true,
  value,
  onOptionSelect,
}: BuySellOptionsProps) => {
  const [options, setOptions] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingValues, setEditingValues] = useState<string[]>([]);

  const storageKey = isNativeToken
    ? "buySellOptions_native"
    : "buySellOptions_percentage";

  useEffect(() => {
    const defaultOptions = isNativeToken
      ? ["0.001", "0.01", "0.1", "1"]
      : ["10", "25", "50", "100"];
    const savedOptions = localStorage.getItem(storageKey);
    if (savedOptions) {
      setOptions(JSON.parse(savedOptions));
    } else {
      setOptions(defaultOptions);
      localStorage.setItem(storageKey, JSON.stringify(defaultOptions));
    }
  }, [isNativeToken, storageKey]);

  const handleOptionClick = (optionValue: string) => {
    if (!isEditing) {
      onOptionSelect(optionValue);
    }
  };

  const handleEditClick = () => {
    if (isEditing) {
      const newOptions = editingValues.map((val, idx) => {
        if (val.trim() === "") {
          return options[idx] || "";
        }
        const numValue = parseFloat(val);
        if (isNaN(numValue) || numValue < 0.001) return "0.001";
        if (numValue > 100) return "100";
        return numValue.toString();
      });
      setOptions(newOptions);
      localStorage.setItem(storageKey, JSON.stringify(newOptions));
      const prevIndex = options.findIndex((opt) => opt === value);
      if (prevIndex !== -1) {
        const newValue = newOptions[prevIndex];
        if (newValue !== value) {
          onOptionSelect(newValue);
        }
      }
      setIsEditing(false);
    } else {
      setEditingValues([...options]);
      setIsEditing(true);
    }
  };

  const handleInputChange = (index: number, value: string) => {
    if (value === "") {
      const newValues = [...editingValues];
      newValues[index] = value;
      setEditingValues(newValues);
      return;
    }

    if (!/^\d*\.?\d*$/.test(value)) return;

    if (value.startsWith(".")) {
      const newValues = [...editingValues];
      newValues[index] = value;
      setEditingValues(newValues);
      return;
    }

    if (value.startsWith("0")) {
      if (value === "0" || value.startsWith("0.")) {
        const newValues = [...editingValues];
        newValues[index] = value;
        setEditingValues(newValues);
        return;
      } else {
        return;
      }
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      if (numValue < 0.001 && value !== "0." && value !== ".") return;
      if (numValue > 100) return;
    }

    const newValues = [...editingValues];
    newValues[index] = value;
    setEditingValues(newValues);
  };

  return (
    <div className="flex h-[1.5rem] items-center gap-x-[0.3125rem]">
      {options.map((option, index) => (
        <GradientBorderRadius
          key={`${option}-${index}`}
          radius="bigger"
          gradient={value === option && !isEditing ? "100" : "default-border"}
          hover="r-100"
          className="w-full h-[1.5rem]"
        >
          <div
            className="flex h-full items-center justify-center gap-x-[0.188rem] cursor-pointer"
            onClick={() => handleOptionClick(option)}
          >
            {isEditing ? (
              <input
                type="number"
                value={editingValues[index] || ""}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleEditClick();
                  }
                }}
                className="text-xs bg-transparent border-none text-center outline-none max-w-[1.875rem] w-auto"
                autoFocus={index === 0}
              />
            ) : (
              <>
                {isNativeToken && (
                  <IconComponent
                    name="solanaLogo"
                    className="w-[0.95rem] h-[0.95rem]"
                  />
                )}
                <span className="text-xs">
                  {option}
                  {!isNativeToken && "%"}
                </span>
              </>
            )}
          </div>
        </GradientBorderRadius>
      ))}
      <GradientBorderRadius
        radius="circle"
        gradient="default-border"
        active={isEditing ? "r-100" : "none"}
        hover="r-100"
        className="h-[1.5rem] w-[1.5rem] shrink-0"
      >
        <button
          className="flex items-center justify-center w-full h-full"
          onClick={handleEditClick}
        >
          <IconComponent name="editPencil" className="w-[0.5rem] h-[0.5rem]" />
        </button>
      </GradientBorderRadius>
    </div>
  );
};

export default BuySellOptions;
